import Box from "@mui/material/Box"; // Using MUI Box for TabPanel
import AppBar from "@mui/material/AppBar";
import Tab from "@mui/material/Tab"; // Using MUI Tab
import Tabs from "@mui/material/Tabs"; // Using MUI Tabs
import Typography from "@mui/material/Typography"; // Using MUI Typography for title
import PropTypes from "prop-types";
import React, { useState, useEffect } from "react";
import StockScreener from "./StockScreener";
import Finviz from "./Finviz";
import ScreenerInsights from "./ScreenerInsights";
import PerformanceView from "./PerformanceView";
import SectorCardView from "./SectorCardView";
import ScreenerCombinedView from "./ScreenerCombinedView";
import ScreenerTopPerformanceCharts from "./ScreenerTopPerformanceCharts";

// MUI TabPanel component
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box
          sx={{
            p: 0,
            width: "calc(100% - 2rem)",
            margin: "0.5rem 1rem 0.25rem 1rem",
          }}
        >
          {children}
        </Box>
      )}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.any.isRequired,
  value: PropTypes.any.isRequired,
};

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

export default function ScreenerContainer(props) {
  const [value, setValue] = useState(0);
  const [portfolioAnalysisData, setPortfolioAnalysisData] = useState([]);

  // Effect to prepare data for the Insights tab
  useEffect(() => {
    if (props.portfolio && props.portfolio.stockReports) {
      // This data will be passed to the ScreenerInsights component
      setPortfolioAnalysisData(props.portfolio.stockReports);
    } else {
      setPortfolioAnalysisData([]);
    }
  }, [props.portfolio]);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  // Get the screener title: explicit prop takes precedence, then portfolio name
  const screenerName =
    props.screenerTitle || props.portfolio?.name || "Screener";

  return (
    <Box sx={{ width: "100%", margin: 0, padding: 0 }}>
      {/* Common Title Bar - visible on all tabs */}
      <Box
        sx={{
          backgroundColor: "primary.main",
          color: "white",
          padding: "0.75rem 1rem",
          minHeight: "48px",
          marginTop: 0,
          display: "flex",
          alignItems: "center",
        }}
      >
        <Typography
          variant="h6"
          component="h2"
          sx={{
            fontWeight: 600,
            fontSize: { xs: "1.1rem", md: "1.25rem" },
            letterSpacing: "-0.01em",
          }}
        >
          {screenerName}
        </Typography>
      </Box>

      <AppBar position="static" color="inherit">
        <Tabs
          value={value}
          onChange={handleChange}
          aria-label="screener tabs"
          variant="scrollable"
          scrollButtons="auto"
          // sx for MUI Tabs to better match Semantic UI's secondary/pointing style if desired
          // This is optional and can be customized further
          sx={{
            "& .MuiTabs-indicator": { backgroundColor: "primary.main" }, // Example indicator color
          }}
        >
          <Tab label="Holdings" {...a11yProps(0)} />
          <Tab label="Snapshots" {...a11yProps(1)} />
          <Tab label="Insights" {...a11yProps(2)} />
          <Tab label="Sector View" {...a11yProps(3)} />
          <Tab label="Performance View" {...a11yProps(4)} />
          <Tab label="Charts" {...a11yProps(5)} />
          <Tab label="Top 10 Stocks By Performance" {...a11yProps(6)} />
        </Tabs>
      </AppBar>

      <TabPanel value={value} index={0}>
        <StockScreener
          portfolio={props.portfolio}
          portfolioImageUrl={props.portfolioImageUrl}
        />
      </TabPanel>
      <TabPanel value={value} index={1}>
        <Box sx={{ width: "100%", mx: 0, mt: 0 }}>
          <ScreenerCombinedView
            stockReports={props.portfolio ? props.portfolio.stockReports : null}
            displayAll={false}
          />
        </Box>
      </TabPanel>
      <TabPanel value={value} index={2}>
        <ScreenerInsights
          name={screenerName}
          portfolioAnalysisData={portfolioAnalysisData}
        />
      </TabPanel>
      <TabPanel value={value} index={3}>
        <Box sx={{ width: "100%", mx: 0, mt: 0 }}>
          <SectorCardView
            stockReports={props.portfolio ? props.portfolio.stockReports : null}
          />
        </Box>
      </TabPanel>
      <TabPanel value={value} index={4}>
        <Box sx={{ width: "100%", mx: 0, mt: 0 }}>
          <PerformanceView
            stockReports={props.portfolio ? props.portfolio.stockReports : null}
          />
        </Box>
      </TabPanel>
      <TabPanel value={value} index={5}>
        <Box sx={{ width: "100%", mx: 0, mt: 0 }}>
          <Finviz
            stockReports={props.portfolio ? props.portfolio.stockReports : null}
            displayAll={false}
            style={{ maxWidth: "95%", margin: "0 auto" }}
          />
        </Box>
      </TabPanel>
      <TabPanel value={value} index={6}>
        <Box sx={{ width: "100%", mx: 0, mt: 0 }}>
          <ScreenerTopPerformanceCharts
            stockReports={props.portfolio ? props.portfolio.stockReports : null}
            topN={10}
          />
        </Box>
      </TabPanel>
    </Box>
  );
}
